/**
 * A minimal history-API router.
 *
 * Three routes did not justify pulling in react-router. The Worker serves
 * index.html for unknown paths (`not_found_handling: single-page-application`),
 * so deep links resolve here rather than 404ing.
 */
import { useCallback, useEffect, useState } from "react";

export type CatalogueTab = "patterns" | "owned" | "plans";

export const CATALOGUE_TAB_PATHS: Record<CatalogueTab, string> = {
  patterns: "/shape",
  owned: "/owned",
  plans: "/plans",
};

export type Route =
  | { name: "home" }
  | { name: "catalogue"; tab: CatalogueTab }
  | { name: "weapon"; recipeHash: number }
  /** Stateless: the whole plan is encoded in the URL. */
  | { name: "plan"; encoded: string }
  /** Server-stored, so it can name whoever shared it. */
  | { name: "shared"; id: string }
  | { name: "not-found"; path: string };

export function parseRoute(pathname: string): Route {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (path === "/") return { name: "home" };
  if (path === "/shape") return { name: "catalogue", tab: "patterns" };
  if (path === "/owned") return { name: "catalogue", tab: "owned" };
  if (path === "/plans") return { name: "catalogue", tab: "plans" };

  const weapon = /^\/weapon\/(\d{1,10})$/.exec(path);
  if (weapon) return { name: "weapon", recipeHash: Number(weapon[1]) };

  // base64url — the alphabet encodePlan produces.
  const plan = /^\/plan\/([A-Za-z0-9_-]+)$/.exec(path);
  if (plan) return { name: "plan", encoded: plan[1]! };

  // A separate path rather than length-sniffing a short id apart from an
  // encoded payload, which would be ambiguous and fragile.
  const shared = /^\/shared\/([A-Za-z0-9_-]{6,32})$/.exec(path);
  if (shared) return { name: "shared", id: shared[1]! };

  return { name: "not-found", path };
}

export function routeToPath(route: Route): string {
  switch (route.name) {
    case "home":
      return "/";
    case "catalogue":
      return CATALOGUE_TAB_PATHS[route.tab];
    case "weapon":
      return `/weapon/${route.recipeHash}`;
    case "plan":
      return `/plan/${route.encoded}`;
    case "shared":
      return `/shared/${route.id}`;
    default:
      return route.path;
  }
}

export function useRoute(): { route: Route; navigate: (path: string) => void } {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));

  useEffect(() => {
    const onPop = () => setRoute(parseRoute(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((path: string) => {
    if (path === window.location.pathname) return;
    window.history.pushState(null, "", path);
    setRoute(parseRoute(path));
    window.scrollTo(0, 0);
  }, []);

  return { route, navigate };
}
